import {Alert, Anchor, Badge, Button, Group, Paper, Stack, Text, Title} from '@mantine/core'
import {useState} from 'react'

import {CustomerPocSelector} from '../customers/customer-poc-selector'
import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import type {JobDetail, JobPatchInput} from './types'

type JobCustomerPocSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

const sourceLabels: Record<string, string> = {
    job: 'Job POC',
    project: 'Order POC',
}

export function JobCustomerPocSection({
    job,
    jobDataLayer,
    onJobUpdated,
}: JobCustomerPocSectionProps) {
    const {canEdit} = useEditRights()
    // Only an explicit job assignment (source 'job') is a persisted override.
    // An inherited (source 'project') effective contact must not be treated as
    // an explicit selection: saving the editor unchanged must not persist it.
    const explicitContactId =
        job.customerContactSource === 'job' ? (job.customerContact?.id ?? null) : null
    const [isEditing, setIsEditing] = useState(false)
    const [selectedContactId, setSelectedContactId] = useState<string | null>(explicitContactId)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    const sourceLabel = job.customerContactSource ? sourceLabels[job.customerContactSource] : null

    async function handleSave() {
        setSaveError(null)
        setIsSaving(true)
        try {
            const patch: JobPatchInput = {
                jobName: job.name,
                serviceTypeId: job.serviceTypeId,
                notes: job.notes,
                customerId: job.customerId,
                customerContactId: selectedContactId,
            }
            const updated = await jobDataLayer.patchJob(job.id, patch)
            onJobUpdated(updated)
            setIsEditing(false)
        } catch (saveFailure) {
            setSaveError(
                saveFailure instanceof Error
                    ? saveFailure.message
                    : 'Failed to update customer point of contact',
            )
        } finally {
            setIsSaving(false)
        }
    }

    function handleCancel() {
        setSelectedContactId(explicitContactId)
        setSaveError(null)
        setIsEditing(false)
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group gap="sm" align="center">
                        <Title order={4}>Customer point of contact</Title>
                        {sourceLabel && !isEditing ? (
                            <Badge size="xs" variant="light" color="grape">
                                {sourceLabel}
                            </Badge>
                        ) : null}
                    </Group>
                    {!isEditing && canEdit ? (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setIsEditing(true)}
                        >
                            {explicitContactId ? 'Reassign' : 'Assign'}
                        </Button>
                    ) : null}
                </Group>

                {isEditing ? (
                    <Stack gap="sm">
                        <CustomerPocSelector
                            customerId={job.customerId}
                            value={selectedContactId}
                            onChange={setSelectedContactId}
                        />
                        {saveError ? (
                            <Alert color="red" title="Could not update customer point of contact">
                                {saveError}
                            </Alert>
                        ) : null}
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={isSaving}
                            >
                                Save
                            </Button>
                        </Group>
                    </Stack>
                ) : job.customerContact ? (
                    <Stack gap={0}>
                        <Text size="sm" fw={500}>
                            {job.customerContact.name}
                        </Text>
                        {job.customerContact.title ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Title:
                                </Text>
                                <Text size="sm">{job.customerContact.title}</Text>
                            </Group>
                        ) : null}
                        {job.customerContact.phone ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Phone:
                                </Text>
                                <Text size="sm">{job.customerContact.phone}</Text>
                            </Group>
                        ) : null}
                        {job.customerContact.email ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Email:
                                </Text>
                                <Anchor href={`mailto:${job.customerContact.email}`} size="sm">
                                    {job.customerContact.email}
                                </Anchor>
                            </Group>
                        ) : null}
                    </Stack>
                ) : (
                    <Text size="sm" c="dimmed">
                        No customer point of contact assigned.
                    </Text>
                )}
            </Stack>
        </Paper>
    )
}
